import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  VStack,
  Text,
  Spinner,
  Flex,
  Alert,
  AlertIcon,
  useToast
} from '@chakra-ui/react';

// Components
import Card from 'components/Card/Card.js';

// Services
import { getDashboardData } from 'services/dashboardService';
import { getEvents } from 'services/agenda';
import { getAllAppointments } from 'services/appointmentBooking';
import { useUserAuth, useRoleModuleAccess, MODULES } from 'utils/site';
import { getChileTodayISO } from 'utils/dateUtils';

// Components específicos del dashboard
import HomeHero from './components/HomeHero';
import AgendaTodayCard from './components/AgendaTodayCard';
import QuickActions from './components/QuickActions';
import ActivityTimeline from './components/ActivityTimeline';
import RecentUsers from './components/RecentUsers';

const DashboardGlobal = () => {
  // Estados principales
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Agenda de hoy (para la tarjeta de agenda)
  const [todayEvents, setTodayEvents] = useState([]);
  const [isLoadingAgenda, setIsLoadingAgenda] = useState(true);

  // Tope de elementos cargados en la tarjeta (hoy + próximos días, nunca "todas").
  // La tarjeta los pagina internamente, por eso el tope puede ser más alto que lo visible por página.
  const AGENDA_PREVIEW_LIMIT = 24;
  const AGENDA_PREVIEW_DAYS = 7;

  const toast = useToast();

  // Obtener información del usuario autenticado y permisos
  const { user } = useUserAuth();
  const hasAgendaAccess = useRoleModuleAccess(MODULES.AGENDA);
  const isAdmin = user?.role === 'administrador';

  // Función principal para cargar datos del dashboard
  const loadDashboardData = useCallback(async () => {
    try {
      setError(null);
      const mainData = await getDashboardData();
      setDashboardData(mainData);
    } catch (error) {
      console.error('❌ DASHBOARD GLOBAL: Error cargando datos:', error);
      setError('Error al cargar los datos del dashboard');

      toast({
        title: "Error al actualizar",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Efecto para carga inicial
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Efecto para auto-actualización cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isLoading) {
        loadDashboardData();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [loadDashboardData, isLoading]);

  // Cargar eventos de agenda de hoy (para el pill del hero y la tarjeta de agenda)
  useEffect(() => {
    if (!hasAgendaAccess) {
      setIsLoadingAgenda(false);
      return;
    }

    const loadTodayEvents = async () => {
      try {
        setIsLoadingAgenda(true);
        const today = getChileTodayISO();
        // Ventana acotada: hoy + próximos N días (nunca "todas" las futuras sin límite)
        const horizon = new Date(`${today}T00:00:00`);
        horizon.setDate(horizon.getDate() + AGENDA_PREVIEW_DAYS);
        const horizonISO = horizon.toISOString().split('T')[0];

        const response = await getEvents({ dateFrom: today, dateTo: horizonISO });
        let items = response?.data || [];

        // Administrador: agregar también las citas (hoy + próximas) de TODOS los profesores
        // (mismo origen de datos que la vista "Todas las citas" del módulo Agenda)
        if (isAdmin) {
          try {
            const apptResponse = await getAllAppointments({ dateFrom: today, dateTo: horizonISO, perPage: 100 });
            const apptEvents = (apptResponse?.data || [])
              .filter((a) => a.status !== 'cancelled')
              .map((a) => ({
                id: `appt-${a.id}`,
                title: `🗓 ${a.student_name || a.guardian_name || 'Sin nombre'}`,
                description: a.teacher_name ? `Con ${a.teacher_name}` : '',
                location: a.teacher_name ? `Prof. ${a.teacher_name}` : '',
                start_date: `${a.appointment_date}T${a.start_time}`,
                end_date: `${a.appointment_date}T${a.end_time}`,
                category: 'cita',
                status: a.status,
                all_day: false,
                _appointment: a,
              }));
            items = [...items, ...apptEvents];
          } catch (apptError) {
            console.error('Error cargando citas de todos los profesores:', apptError);
          }
        }

        const sorted = items
          .slice()
          .sort((a, b) => new Date(a.start_date || 0) - new Date(b.start_date || 0));

        // La tarjeta muestra hoy + próximas, acotado a un máximo (no todas)
        setTodayEvents(sorted.slice(0, AGENDA_PREVIEW_LIMIT));
      } catch (error) {
        console.error('Error cargando agenda de hoy:', error);
        setTodayEvents([]);
      } finally {
        setIsLoadingAgenda(false);
      }
    };

    loadTodayEvents();
  }, [hasAgendaAccess, isAdmin]);

  // Loading state
  if (isLoading && !dashboardData) {
    return (
      <Flex
        justify="center"
        align="center"
        minH="400px"
        direction="column"
        pt={{ base: "120px", md: "75px" }}
      >
        <Spinner size="xl" color="coral.500" thickness="4px" />
        <Text mt={4} fontSize="lg" color="ink.400">
          Cargando dashboard...
        </Text>
      </Flex>
    );
  }

  // Error state
  if (error && !dashboardData) {
    return (
      <Box pt={{ base: "120px", md: "75px" }}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Error en el Dashboard</Text>
            <Text fontSize="sm">{error}</Text>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "120px", md: "75px" }} fontFamily="jakarta">
      <VStack spacing="18px" align="stretch">
        <HomeHero user={user} />

        <Grid templateColumns={{ base: "1fr", lg: "1.48fr 1fr" }} gap="18px">
          {/* Columna izquierda: Agenda de hoy + Alumnos recientes */}
          <VStack spacing="18px" align="stretch">
            {hasAgendaAccess && (
              <AgendaTodayCard events={todayEvents} isLoading={isLoadingAgenda} />
            )}
            <RecentUsers />
          </VStack>

          {/* Columna derecha: Acciones rápidas + Actividad reciente */}
          <VStack spacing="18px" align="stretch">
            <QuickActions onActionComplete={loadDashboardData} />

            <Card p={0} borderRadius="22px" overflow="hidden">
              <Box p="18px 22px 6px">
                <Text fontSize="16px" fontWeight="800" letterSpacing="-0.01em">
                  Actividad reciente
                </Text>
              </Box>
              <Box p="0 14px 14px">
                <ActivityTimeline
                  data={dashboardData}
                  isLoading={isLoading}
                  compact={true}
                />
              </Box>
            </Card>
          </VStack>
        </Grid>
      </VStack>
    </Box>
  );
};

export default DashboardGlobal;
